import {
  ESenderRole,
  IContractDetailsResponseData,
  UserParty,
} from "../api/services/contract/models";
import { IApiResponse } from "../api/models";
import { userMatchesIdentifier } from "./userIdentifier";

/**
 * WAF-3109 — "refund back to source contract" cashout.
 *
 * A contract funded by a transfer (`sourceContractId`) can be refunded straight
 * back into the wallet contract the customer paid from. The backend creates that
 * destination contract, so the request carries `createBackToSourceContract: true`
 * and no `targetContractDomainId`.
 */

/**
 * The ids the source contract answers to — its Mongo `id` and its `shortId`.
 *
 * Only needed as a *fallback*: today `contract.sourceContractId` arrives as a
 * `shortId` while a party's `referenceContractId` is a Mongo id, so the direct
 * comparison can't link them and the missing id has to be looked up. If the
 * backend ever sends both fields in the same shape, the direct comparison
 * matches on its own and this identity is never resolved.
 */
export interface SourceContractIdentity {
  /** Mongo id of the source contract. */
  id?: string;
  /** Short id of the source contract. */
  shortId?: string;
}

/** Compares two contract ids of the *same* shape, tolerating case/whitespace. */
export const contractIdsMatch = (a?: string, b?: string): boolean =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Finds the CUSTOMER party that funded this contract from its source contract,
 * matching the party's `referenceContractId` against the source contract in
 * three layers:
 *
 * 1. Directly against `contract.sourceContractId`. This is the only layer that
 *    runs when `sourceContract` is omitted, and the only one needed once both
 *    fields carry the same id shape.
 * 2. Against the source contract's other id, when the caller resolved it (see
 *    `useSourceContract`) because layer 1 found nothing. Needed today: the
 *    contract carries the source's `shortId`, the party its Mongo id.
 *
 * No layer matches on "the party paid by transfer" alone: that is true of every
 * transfer-funded customer, so it offers the refund to customers who funded
 * from a *different* contract, and contract-api then rejects the cashout with
 * `CUSTOMER_NOT_SOURCE_CASHIN_PARTY`. When the link can't be proven — the
 * lookup failed — the party is not returned and the action stays hidden.
 */
export const findSourceCashInCustomerParty = (
  contract?: IContractDetailsResponseData,
  beneficiaryId?: string | number,
  sourceContract?: SourceContractIdentity
): UserParty | undefined => {
  if (!contract?.sourceContractId || beneficiaryId === undefined) {
    return undefined;
  }

  const sourceAliases = [
    contract.sourceContractId,
    sourceContract?.id,
    sourceContract?.shortId,
  ].filter(Boolean) as string[];

  return contract.parties?.find(
    (party) =>
      party.role === ESenderRole.CUSTOMER &&
      userMatchesIdentifier(party, beneficiaryId) &&
      sourceAliases.some((alias) =>
        contractIdsMatch(party.referenceContractId, alias)
      )
  );
};

/**
 * True when the refund for `beneficiaryId` can go back to the source contract:
 * the contract was funded by a transfer, and this customer is the party that
 * funded it from that source contract.
 *
 * Call it without `sourceContract` first; only resolve one and call again if
 * that direct comparison comes back false.
 */
export const canRefundBackToSourceContract = (
  contract?: IContractDetailsResponseData,
  beneficiaryId?: string | number,
  sourceContract?: SourceContractIdentity
): boolean =>
  !!findSourceCashInCustomerParty(contract, beneficiaryId, sourceContract);

/** Machine-readable codes contract-api appends as a trailing `(code: X)`. */
export enum EBackToSourceErrorCode {
  NOT_A_TRANSFER_TARGET_CONTRACT = "NOT_A_TRANSFER_TARGET_CONTRACT",
  CUSTOMER_NOT_ON_CONTRACT = "CUSTOMER_NOT_ON_CONTRACT",
  CUSTOMER_NOT_SOURCE_CASHIN_PARTY = "CUSTOMER_NOT_SOURCE_CASHIN_PARTY",
  SOURCE_NOT_MILESTONE = "SOURCE_NOT_MILESTONE",
  SOURCE_NOT_BUYER_ONLY = "SOURCE_NOT_BUYER_ONLY",
}

/** Translation key per error code, for messages we can phrase better than the API. */
const ERROR_CODE_LABEL_KEYS: Record<EBackToSourceErrorCode, string> = {
  [EBackToSourceErrorCode.NOT_A_TRANSFER_TARGET_CONTRACT]:
    "lbl_back_to_source_error_not_transfer_target",
  [EBackToSourceErrorCode.CUSTOMER_NOT_ON_CONTRACT]:
    "lbl_back_to_source_error_customer_not_on_contract",
  [EBackToSourceErrorCode.CUSTOMER_NOT_SOURCE_CASHIN_PARTY]:
    "lbl_back_to_source_error_customer_not_source_party",
  [EBackToSourceErrorCode.SOURCE_NOT_MILESTONE]:
    "lbl_back_to_source_error_source_not_cloneable",
  [EBackToSourceErrorCode.SOURCE_NOT_BUYER_ONLY]:
    "lbl_back_to_source_error_source_not_cloneable",
};

/** Reads the trailing `(code: X)` out of a contract-api error message. */
export const parseBackToSourceErrorCode = (
  message?: string
): EBackToSourceErrorCode | undefined => {
  const code = message?.match(/\(code:\s*([A-Z_]+)\)/)?.[1];
  return code && code in EBackToSourceErrorCode
    ? (code as EBackToSourceErrorCode)
    : undefined;
};

/**
 * Message to show for a failed back-to-source cashout. Codes we recognise get
 * our own copy; anything else falls back to the API message, then to `fallback`.
 */
export const getBackToSourceErrorMessage = (
  error: unknown,
  translate: (key: string) => string,
  fallback: string
): string => {
  const apiMessage = (error as IApiResponse)?.error?.message;
  const code = parseBackToSourceErrorCode(apiMessage);

  if (code) {
    return translate(ERROR_CODE_LABEL_KEYS[code]);
  }

  return apiMessage || fallback;
};
